// Push notification settings — toggle inside SettingsPanel (variant="panel")
// and a one-shot banner after the user opens the app a few times
// (variant="banner"). Wraps the W3C Push Manager API + our /api/push/* endpoints.
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Bell, BellOff, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { t } from "@/utils/i18n";

const BANNER_DISMISS_KEY = "ac_push_banner_dismissed_until";
const BANNER_DAYS = 30;
const BANNER_MIN_SESSIONS = 3; // re-uses ac_session_count from PWAInstallPrompt

function urlBase64ToUint8Array(b64) {
  // VAPID pubkey is URL-safe base64 (no padding) → convert to Uint8Array
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function permissionState() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

function bannerIsDismissed() {
  try {
    const raw = localStorage.getItem(BANNER_DISMISS_KEY);
    return !!raw && Date.now() < parseInt(raw, 10);
  } catch { return false; }
}
function bannerDismiss() {
  try {
    localStorage.setItem(BANNER_DISMISS_KEY, String(Date.now() + BANNER_DAYS * 86400000));
  } catch { /* noop */ }
}

export default function PushNotificationSettings({ variant = "panel", lang = "en" }) {
  const { token, API } = useAuth();
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState(permissionState());
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(false);
  const isIt = (lang || "en").startsWith("it");

  const refreshStatus = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/push/status`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 8000,
      });
      setActive(!!res.data?.active);
    } catch { /* silent */ }
  }, [token, API]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || typeof Notification === "undefined") {
      setSupported(false);
      return;
    }
    setPermission(Notification.permission);
    refreshStatus();
  }, [refreshStatus]);

  // Banner gating
  useEffect(() => {
    if (variant !== "banner") return;
    if (!supported) return;
    if (permission === "granted" || active) return;
    if (bannerIsDismissed()) return;
    try {
      const sessions = parseInt(localStorage.getItem("ac_session_count") || "0", 10);
      if (sessions >= BANNER_MIN_SESSIONS) setBannerVisible(true);
    } catch { /* noop */ }
  }, [variant, supported, permission, active]);

  const subscribe = async () => {
    setBusy(true);
    try {
      // 1. Ask permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error(isIt ? "Permesso negato" : "Permission denied");
        return;
      }
      // 2. Fetch VAPID public key
      const keyRes = await axios.get(`${API}/push/vapid-public-key`, { timeout: 8000 });
      const vapidPublic = keyRes.data?.public_key;
      if (!vapidPublic) throw new Error("No VAPID key");
      // 3. Register / get SW
      const reg = await navigator.serviceWorker.ready;
      // 4. Subscribe (idempotent — getSubscription first to reuse)
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublic),
        });
      }
      // 5. Send subscription to backend
      const subJson = sub.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
        expirationTime: subJson.expirationTime,
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 });
      setActive(true);
      setBannerVisible(false);
      toast.success(isIt ? "Notifiche attivate" : "Notifications enabled");
    } catch (e) {
      console.error("[push] subscribe failed:", e);
      toast.error((isIt ? "Attivazione fallita: " : "Subscribe failed: ") + (e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const unsubscribe = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await sub.unsubscribe();
        try {
          await axios.post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint }, {
            headers: { Authorization: `Bearer ${token}` }, timeout: 8000,
          });
        } catch { /* swallow */ }
      }
      setActive(false);
      toast.success(isIt ? "Notifiche disattivate" : "Notifications disabled");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/push/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 8000,
      });
      toast.success(isIt
        ? `Test inviato (${r.data.sent || 0} dispositivi)`
        : `Test sent (${r.data.sent || 0} devices)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || (isIt ? "Test fallito" : "Test failed"));
    } finally {
      setBusy(false);
    }
  };

  // ----- BANNER variant -----
  if (variant === "banner") {
    if (!bannerVisible) return null;
    return (
      <div
        className="fixed bottom-3 left-3 right-3 sm:left-auto sm:right-4 sm:bottom-4 sm:w-[360px] z-[140] bg-[#1a1a1d] border-2 border-[#cfb53b]/50 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.7)] p-3.5 flex items-start gap-3"
        data-testid="push-banner"
      >
        <div className="shrink-0 w-10 h-10 rounded-lg bg-[#cfb53b]/15 border border-[#cfb53b]/40 flex items-center justify-center">
          <Bell size={18} className="text-[#cfb53b]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-heading text-sm text-[#cfb53b] leading-tight">
            {isIt ? "Ricevi notifiche di scambio" : "Get trade notifications"}
          </p>
          <p className="text-[11px] text-[#cbd5e1]/90 leading-snug mt-0.5">
            {isIt
              ? "Ti avviseremo quando un altro avventuriero ti propone uno scambio."
              : "We'll let you know when a fellow traveler proposes a trade."}
          </p>
          <div className="flex items-center gap-2 mt-2.5">
            <button
              onClick={subscribe}
              disabled={busy}
              data-testid="push-banner-enable"
              className="bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading text-xs px-3 py-1.5 rounded-md transition-colors disabled:opacity-60 flex items-center gap-1.5"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Bell size={12} />}
              {isIt ? "Attiva" : "Enable"}
            </button>
            <button
              onClick={() => { bannerDismiss(); setBannerVisible(false); }}
              data-testid="push-banner-dismiss"
              className="text-xs text-[#94a3b8] hover:text-[#cfb53b] transition-colors px-2 py-1.5"
            >
              {isIt ? "Più tardi" : "Later"}
            </button>
          </div>
        </div>
        <button
          onClick={() => { bannerDismiss(); setBannerVisible(false); }}
          className="text-[#94a3b8] hover:text-[#cfb53b] shrink-0 -mr-1 -mt-1"
          aria-label="Dismiss"
          data-testid="push-banner-close"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  // ----- PANEL variant -----
  if (!supported) {
    return (
      <div className="space-y-2 bg-[#0a0a0c] border border-[#2d3748] rounded-lg p-3" data-testid="push-settings">
        <div className="flex items-center gap-2">
          <BellOff size={14} className="text-[#94a3b8]" />
          <p className="font-heading text-sm text-[#94a3b8]">
            {isIt ? "Notifiche" : "Notifications"}
          </p>
        </div>
        <p className="text-[10px] text-[#64748b]">
          {isIt
            ? "Il tuo browser non supporta le notifiche push."
            : "Your browser does not support push notifications."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2 bg-[#0a0a0c] border border-[#2d3748] rounded-lg p-3" data-testid="push-settings">
      <div className="flex items-center gap-2">
        {active ? <Bell size={14} className="text-[#cfb53b]" /> : <BellOff size={14} className="text-[#94a3b8]" />}
        <p className="font-heading text-sm text-[#cfb53b]">
          {isIt ? "Notifiche push" : "Push notifications"}
        </p>
      </div>

      {permission === "denied" && (
        <p className="text-[10px] text-[#f87171]">
          {isIt
            ? "Permesso bloccato dal browser. Sblocca dalle impostazioni del sito."
            : "Permission blocked. Unblock it from your browser site settings."}
        </p>
      )}

      <p className="text-[11px] text-[#cbd5e1]/80">
        {active
          ? (isIt ? "Riceverai una notifica quando ti viene proposto uno scambio." : "You'll receive a notification when someone proposes a trade.")
          : (isIt ? "Attiva per ricevere notifiche di scambio in tempo reale." : "Enable to receive real-time trade notifications.")}
      </p>

      <div className="flex gap-2">
        {active ? (
          <button
            onClick={unsubscribe}
            disabled={busy}
            data-testid="push-disable-btn"
            className="flex-1 bg-[#1a1a1d] border border-[#94a3b8]/30 hover:border-[#94a3b8]/60 text-[#cbd5e1] font-heading text-xs px-3 py-2 rounded-md transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <BellOff size={12} />}
            {isIt ? "Disattiva" : "Disable"}
          </button>
        ) : (
          <button
            onClick={subscribe}
            disabled={busy || permission === "denied"}
            data-testid="push-enable-btn"
            className="flex-1 bg-[#cfb53b]/15 hover:bg-[#cfb53b]/25 border border-[#cfb53b]/40 text-[#cfb53b] font-heading text-xs px-3 py-2 rounded-md transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Bell size={12} />}
            {isIt ? "Attiva" : "Enable"}
          </button>
        )}
        {active && (
          <button
            onClick={sendTest}
            disabled={busy}
            data-testid="push-test-btn"
            className="bg-[#0a0a0c] border border-[#cfb53b]/30 hover:border-[#cfb53b]/60 text-[#cfb53b] text-xs px-3 py-2 rounded-md transition-colors disabled:opacity-50"
          >
            {isIt ? "Test" : "Test"}
          </button>
        )}
      </div>
    </div>
  );
}
